/**
 * protocolCheck.js — «Протокол-контроль» [6.0.5].
 * Автопроверка загруженного исследования по врачебному чек-листу:
 *   1. 3D SPGR / T1 до и после контрастирования, толщина 1 мм,
 *      аксиальная и фронтальная проекции, ПЕРВИЧНЫЕ (не реконструированные);
 *   2. T2 с толщиной срезов 2 мм;
 *   3. Расстояние между срезами — 0 (перекрытие тоже засчитывается);
 *   4. Геометрические искажения/артефакты — только ручная оценка (программа
 *      честно помечает пункт как неавтоматизируемый).
 * Все данные — из DICOM-тегов (проверено на реальном файле GE SIGNA Architect).
 */
import {
  buildRows, summarize, STATUS_ICONS,
  OK, WARN, FAIL, MANUAL, contrastFromTags,
} from './protocolCheckCore.js';
import { cornerstone } from '../core/init.js';
import { getCachedDataSet } from '../loaders/loaderManager.js';
import { buildStudyIndex, sortStudiesByDate, formatStudyTitle } from '../core/studyIndex.js';
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';

let _getAllSeries = () => [];

// ── Извлечение параметров серии из тегов ──────────────────────

function _tagReader(imageId) {
  let ds = null, nat = null;
  try { ds = imageId ? getCachedDataSet(imageId) : null; } catch (e) { ds = null; }
  try { nat = imageId ? cornerstone.metaData.get('instance', imageId) : null; } catch (e) { nat = null; }
  return (tagHex, kw) => {
    try { const v = ds?.string(tagHex); if (v && v.trim()) return v.trim(); } catch (e) { /* нет в кэше */ }
    let nv = nat ? nat[kw] : undefined;
    if (nv == null) return '';
    if (Array.isArray(nv)) nv = nv[0];
    return String(nv).trim();
  };
}

/** Нормаль среза из IOP (6 направляющих косинусов) */
function _normalFromIOP(iop) {
  if (!Array.isArray(iop) || iop.length !== 6 || !iop.every(Number.isFinite)) return null;
  const [r1, r2, r3, c1, c2, c3] = iop;
  return [r2 * c3 - r3 * c2, r3 * c1 - r1 * c3, r1 * c2 - r2 * c1];
}

/** Плоскость съёмки: (1) iop из записей импорта; (2) тег 0020,0037 из dataSet.
 *  [6.0.8 FIX] Раньше читали rowCosines из метаданных Cornerstone — наш
 *  собственный провайдер (приоритет 20000) это поле не отдаёт, плоскость
 *  была пустой у ВСЕХ серий. */
function _planeOf(imgs, t) {
  let iop = imgs.find(im => Array.isArray(im.iop))?.iop;
  if (!iop) {
    const raw = t('x00200037', 'ImageOrientationPatient');
    if (raw) iop = String(raw).split('\\').map(Number);
  }
  const n = _normalFromIOP(iop);
  if (!n) return '';
  const a = n.map(Math.abs);
  return ['sagittal', 'coronal', 'axial'][a.indexOf(Math.max(a[0], a[1], a[2]))];
}

/** Фактический шаг между срезами: проекция разности ipp соседних кадров на нормаль.
 *  ipp/iop берутся из записей импорта — Cornerstone не нужен. */
function _actualSpacing(imgs) {
  try {
    const k = Math.floor(imgs.length / 2);
    const a = imgs[k]?.ipp, b = imgs[k + 1]?.ipp;
    const n = _normalFromIOP(imgs[k]?.iop);
    if (Array.isArray(a) && Array.isArray(b) && n) {
      return Math.abs((b[0] - a[0]) * n[0] + (b[1] - a[1]) * n[1] + (b[2] - a[2]) * n[2]);
    }
    // Фолбэк: разность SliceLocation соседних кадров
    const s1 = imgs[k]?.sliceLocation, s2 = imgs[k + 1]?.sliceLocation;
    if (Number.isFinite(s1) && Number.isFinite(s2) && s1 !== s2) return Math.abs(s2 - s1);
  } catch (e) { /* нет данных */ }
  return NaN;
}

/** [6.0.8] Догрузка одного среднего кадра на серию: кэш dataSet подрезается
 *  (trimDataSetCache), поэтому TR/TE/ImageType у невыгруженных серий были «?».
 *  После loadAndCacheImage метаданные 'instance' доступны для анализа. */
async function _ensureMeta(seriesList) {
  const need = [];
  for (const s of seriesList) {
    const imgs = s.images || s.files || [];
    const id = imgs[Math.floor(imgs.length / 2)]?.imageId || imgs[0]?.imageId;
    if (!id) continue;
    let has = false;
    try { has = !!getCachedDataSet(id); } catch (e) { has = false; }
    if (!has) { try { has = !!cornerstone.metaData.get('instance', id); } catch (e) { has = false; } }
    if (!has) need.push(id);
  }
  if (!need.length) return;
  await Promise.race([
    Promise.allSettled(need.map(id => cornerstone.imageLoader.loadAndCacheImage(id))),
    new Promise(r => setTimeout(r, 12000)), // страховка: отчёт не зависает
  ]);
}

function analyzeSeries(s) {
  const imgs = s.images || s.files || [];
  const id = imgs[Math.floor(imgs.length / 2)]?.imageId || imgs[0]?.imageId;
  const t = _tagReader(id);

  const desc = String(s.seriesDesc || t('x0008103e', 'SeriesDescription') || '');
  let psd = '';
  try { psd = (getCachedDataSet(id)?.string('x0019109c') || '').trim(); } catch (e) { psd = ''; }

  const itRaw = t('x00080008', 'ImageType');
  let itFull = itRaw;
  try {
    const natIT = cornerstone.metaData.get('instance', id)?.ImageType;
    if (Array.isArray(natIT)) itFull = natIT.join('\\');
  } catch (e) { /* строки достаточно */ }
  const IT = String(itFull).toUpperCase();
  const isPrimary = IT.includes('ORIGINAL') && IT.includes('PRIMARY');
  const isDerived = IT.includes('DERIVED') || IT.includes('SECONDARY') || IT.includes('REFORMAT');
  const isLocalizer = IT.includes('LOCALIZER') || IT.includes('SCOUT') || /LOCALIZ|SCOUT|3.?PLANE|\bLOC\b/i.test(desc);

  let thk = parseFloat(t('x00180050', 'SliceThickness'));
  // [10.19.1] Фолбэк — СЫРОЙ тег из записи импорта. Прежний код читал
  // sliceThickness, куда загрузчики подставляют 1 при пустом теге, и серия с
  // НЕИЗВЕСТНОЙ толщиной проходила требование «1 мм» как годная. Теперь при
  // пустом теге толщина остаётся NaN, и thicknessStatus честно даёт FAIL.
  if (!Number.isFinite(thk)) {
    const запись = imgs[0] || {};
    const сырое = ('sliceThicknessRaw' in запись) ? запись.sliceThicknessRaw : запись.sliceThickness;
    const et = parseFloat(сырое);
    if (Number.isFinite(et)) thk = et;
  }
  const sbs = parseFloat(t('x00180088', 'SpacingBetweenSlices'));
  const act = _actualSpacing(imgs);
  const spacing = Number.isFinite(act) ? act : sbs;
  const gap = (Number.isFinite(spacing) && Number.isFinite(thk)) ? spacing - thk : NaN;

  const tr = parseFloat(t('x00180080', 'RepetitionTime'));
  const te = parseFloat(t('x00180081', 'EchoTime'));
  const ti = parseFloat(t('x00180082', 'InversionTime'));
  const fa = parseFloat(t('x00181314', 'FlipAngle'));

  // [6.1.0] Ангио-флаг (0018,0025)=Y — надёжный признак MRA/MRV на любой технике
  const angio = /^Y/i.test(t('x00180025', 'AngioFlag'));
  // Steady-state семейство (FIESTA у GE = CISS у Siemens): PSD-имя + физика b-SSFP.
  // GE-тег последовательности (0019,109E) семейство, (0019,109C) PSD.
  let fam = '';
  try { fam = (getCachedDataSet(id)?.string('x0019109e') || '').trim(); } catch (e) { fam = ''; }
  const seqTxt = (psd + ' ' + fam).toUpperCase();
  const isSSFP = /FIESTA|CISS|BFFE|BALANCED|TRUFI|SSFP|3DSSFP/.test(seqTxt)
    // b-SSFP физика: очень короткие TR/TE при большом угле
    || (Number.isFinite(tr) && tr < 12 && Number.isFinite(te) && te < 6 && Number.isFinite(fa) && fa >= 30);

  // Классификация взвешенности: сначала спец-типы, потом текст (описание + PSD), потом физика
  const txt = (desc + ' ' + psd).toUpperCase();
  let kind = 'other';
  if (isLocalizer) kind = 'scout';
  else if (angio || /\bMRA\b|\bMRV\b|\bTOF\b|ANGIO|ВЕНОГР|АНГИО/i.test(txt)) kind = 'mra';
  // [10.19.1] «GRE» — это про технику съёмки, а не про взвешенность: T1 3D
  // тоже градиентное эхо. Явная пометка T1/T2/FLAIR сильнее слова GRE, иначе
  // «AX 3D GRE T1 +C» уезжал в SWI, и требование «T1 с контрастом» падало
  // «серия не найдена». T2* остаётся SWI всегда — это и есть susceptibility.
  else if (/\bSWI\b|SWAN|T2\*|ESWAN|SUSC|HEMO/i.test(txt)
    || (/\bGRE\b/i.test(txt) && !/\bT1\b|MPRAGE|BRAVO|SPGR|\bTFL\b|FLAIR|\bT2\b(?!\*)/i.test(txt))) kind = 'swi';
  else if (isSSFP) kind = 'ssfp';
  else if (/ADC|DWI|TRACE|\bB-?\d{3,4}\b|DIFF/.test(txt)) kind = 'dwi';
  else if (/FLAIR/.test(txt) || (Number.isFinite(ti) && ti > 1500)) kind = 'flair';
  else if (/T2|PROP/.test(txt) || (Number.isFinite(te) && te > 60)) kind = 't2';
  else if (/T1|SPGR|BRAVO|MPRAGE|TFL/.test(txt) || (Number.isFinite(te) && te < 15 && Number.isFinite(tr) && tr < 1500)) kind = 't1';

  // [10.19.1] Правило контраста — в ядре (protocolCheckCore.contrastFromTags),
  // под тестом: раньше «T1 PRE CONTRAST», «NO CONTRAST» и тег «NONE» давали
  // «с контрастом», и вердикты о до/после контраста были ложные.
  const hasContrast = contrastFromTags(t('x00180010', 'ContrastBolusAgent'), desc);

  return {
    // [10.17.0] Без studyUID сверка не может отличить свои серии от чужих —
    // и в режиме сравнения закрывает требования июля августовскими сериями.
    uid: s.seriesUID, studyUID: s.studyUID || '', desc, count: imgs.length,
    plane: _planeOf(imgs, t), kind, hasContrast, angio, isSSFP,
    thk, spacing, gap, isPrimary, isDerived, isLocalizer,
    tr, te,
  };
}

// ── Сверка с чек-листом ───────────────────────────────────────

// Пороги, значки и сама сверка живут в protocolCheckCore — там их проверяет
// тест. Держать вторую копию здесь значит однажды поправить одну из двух.
const ICONS = STATUS_ICONS;

// ── Профили протоколов ────────────────────────────────────────
// Требование (req) описывается декларативно. Списки серий и пороги должен
// утверждать рентгенолог/нейрохирург центра — здесь клинически обоснованные
// значения по умолчанию, которые легко править.
//   kind        — тип серии ('t1'|'t2'|'flair'|'mra'|'ssfp'|'dwi')
//   plane       — 'axial'|'coronal'|'sagittal' (иначе любая)
//   contrast    — true/false (иначе любой)
//   maxThk      — максимальная толщина среза, мм
//   needGap0    — требовать интервал 0 (перекрытие допустимо)
//   needPrimary — требовать первичность серии

export const PROTOCOL_PROFILES = {
  standard: {
    name: 'Стандарт головы',
    reqs: [
      { label: 'T1/SPGR аксиально · 1 мм · ДО контраста · первично', kind: 't1', plane: 'axial', contrast: false, maxThk: 1.0, needGap0: true, needPrimary: true },
      { label: 'T1/SPGR аксиально · 1 мм · ПОСЛЕ контраста · первично', kind: 't1', plane: 'axial', contrast: true, maxThk: 1.0, needGap0: true, needPrimary: true },
      { label: 'T1/SPGR фронтально · 1 мм · ДО контраста · первично', kind: 't1', plane: 'coronal', contrast: false, maxThk: 1.0, needGap0: true, needPrimary: true },
      { label: 'T1/SPGR фронтально · 1 мм · ПОСЛЕ контраста · первично', kind: 't1', plane: 'coronal', contrast: true, maxThk: 1.0, needGap0: true, needPrimary: true },
      { label: 'T2 · толщина 2 мм · интервал 0', kind: 't2', maxThk: 2.0, needGap0: true },
    ],
  },
  petroclival: {
    name: 'Петрокливальная зона / ЧН-конфликт',
    note: 'Сосуды (MRA/MRV), нервы и цистерны (FIESTA/CISS), опухоль (3D T1+C). Список утверждается врачом центра.',
    reqs: [
      { label: 'MRA / MRV — сосуды основания (обрастание базилярной артерии)', kind: 'mra' },
      { label: 'FIESTA / CISS — тонкосрезная (\u22641 мм), черепные нервы и цистерны', kind: 'ssfp', maxThk: 1.0 },
      { label: '3D T1 ПОСЛЕ контраста · 1 мм · первично — опухоль, дуральное прикрепление', kind: 't1', contrast: true, maxThk: 1.0, needPrimary: true },
      { label: 'T2 аксиально — консистенция опухоли, отёк', kind: 't2', plane: 'axial' },
    ],
  },

  pituitary: {
    name: 'Гипофиз (селлярная область)',
    note: '\u26a0 ЧЕРНОВИК по опубликованным протоколам (корональные тонкосрезные T1/T2 через турецкое седло, динамика с контрастом, постконтраст). Пороги и состав ОБЯЗАН утвердить рентгенолог центра.',
    reqs: [
      { label: 'Корональная T1 \u2264 3 мм · ДО контраста — базовый сигнал железы', kind: 't1', plane: 'coronal', contrast: false, maxThk: 3.0 },
      { label: 'Корональная T2 \u2264 3 мм — хиазма, кисты, отёк', kind: 't2', plane: 'coronal', maxThk: 3.0 },
      { label: 'Корональная T1 \u2264 3 мм · ПОСЛЕ контраста — аденома', kind: 't1', plane: 'coronal', contrast: true, maxThk: 3.0 },
      { label: 'Сагиттальная T1 · ПОСЛЕ контраста — распространение к хиазме', kind: 't1', plane: 'sagittal', contrast: true },
    ],
  },

  epilepsy: {
    name: 'Эпилепсия (HARNESS-MRI, ILAE)',
    note: '\u26a0 ЧЕРНОВИК по консенсусу ILAE (Bernasconi 2019): 3 ядровые последовательности — 3D T1 1 мм изотропно, 3D FLAIR, 2D корональная T2 перпендикулярно длинной оси гиппокампа. Утверждает врач центра.',
    reqs: [
      { label: '3D T1 · изотропно 1 мм — морфология коры', kind: 't1', maxThk: 1.0 },
      { label: '3D FLAIR — фокальные кортикальные дисплазии, глиоз', kind: 'flair' },
      { label: 'Корональная T2 (перпендикулярно оси гиппокампа) — склероз', kind: 't2', plane: 'coronal' },
    ],
  },

  trigeminal: {
    name: 'Тригеминальная невралгия / нейроваскулярный конфликт',
    note: '\u26a0 ЧЕРНОВИК по протоколам МКУ (Radiopaedia): тонкосрезная FIESTA/CISS для нерва и цистерны + MRA для конфликтующего сосуда. Утверждает врач центра.',
    reqs: [
      { label: 'FIESTA / CISS — тонкосрезная (\u2264 1 мм), тройничный нерв в цистерне', kind: 'ssfp', maxThk: 1.0 },
      { label: 'MRA — конфликтующая артерия (чаще верхняя мозжечковая)', kind: 'mra' },
      { label: '3D T1 ПОСЛЕ контраста — исключение объёмного образования в области МКУ', kind: 't1', contrast: true },
    ],
  },

  stroke: {
    name: 'Инсульт (острый ишемический)',
    note: '\u26a0 ЧЕРНОВИК по опубликованным протоколам (JACR/Stroke/AHA): DWI+ADC, FLAIR, SWI/T2*, TOF-MRA. Утверждает врач центра.',
    reqs: [
      { label: 'DWI / ADC — ядро инфаркта, острейшая фаза', kind: 'dwi' },
      { label: 'FLAIR — возраст очага, DWI-FLAIR mismatch', kind: 'flair' },
      { label: 'SWI / T2* — геморрагический компонент', kind: 'swi' },
      { label: 'MRA (TOF) — окклюзия/стеноз сосуда', kind: 'mra' },
    ],
  },

  ms: {
    name: 'Рассеянный склероз (MAGNIMS)',
    note: '\u26a0 ЧЕРНОВИК по консенсусу MAGNIMS-CMSC-NAIMS 2021: ядро — 3D T2-FLAIR (1 мм изотропно); T2 в двух плоскостях; T1 до/после Gd. Утверждает врач центра.',
    reqs: [
      { label: '3D T2-FLAIR \u2264 1 мм — ядровая последовательность MAGNIMS', kind: 'flair', maxThk: 1.0 },
      { label: 'T2 (аксиально) — очаги демиелинизации', kind: 't2', plane: 'axial' },
      { label: 'T1 · ДО контраста — «чёрные дыры»', kind: 't1', contrast: false },
      { label: 'T1 · ПОСЛЕ контраста — активные очаги', kind: 't1', contrast: true },
    ],
  },

  mass: {
    name: 'Объёмное образование головного мозга',
    note: '\u26a0 ЧЕРНОВИК общий онкопротокол: T2/FLAIR, DWI, T1 до и после контраста в разных плоскостях. Точный набор (перфузия, спектроскопия) утверждает врач центра.',
    reqs: [
      { label: 'T2 — структура, отёк', kind: 't2' },
      { label: 'FLAIR — перифокальный отёк, инфильтрация', kind: 'flair' },
      { label: 'DWI / ADC — клеточность опухоли', kind: 'dwi' },
      { label: 'T1 · ДО контраста', kind: 't1', contrast: false },
      { label: 'T1 · ПОСЛЕ контраста — накопление', kind: 't1', contrast: true },
    ],
  },

  cpa: {
    name: 'Невринома слухового нерва / МКУ',
    note: '\u26a0 ЧЕРНОВИК протокол внутреннего слухового прохода: тонкосрезная FIESTA/CISS (цистернография) + постконтрастный T1 через МКУ. Утверждает врач центра.',
    reqs: [
      { label: 'FIESTA / CISS — тонкосрезная (\u2264 1 мм), VII/VIII нервы в МКУ', kind: 'ssfp', maxThk: 1.0 },
      { label: 'T1 ПОСЛЕ контраста — накопление опухолью', kind: 't1', contrast: true },
      { label: 'T2 аксиально — обзор задней черепной ямки', kind: 't2', plane: 'axial' },
    ],
  },

  orbit: {
    name: 'Орбиты',
    note: '\u26a0 ЧЕРНОВИК протокол орбит: тонкосрезные T1/T2 с подавлением жира, постконтрастный T1 FS. Плоскости и подавление жира утверждает врач центра.',
    reqs: [
      { label: 'T2 (жироподавление) \u2264 3 мм — зрительный нерв, мышцы', kind: 't2', maxThk: 3.0 },
      { label: 'T1 · ДО контраста — базовый сигнал', kind: 't1', contrast: false },
      { label: 'T1 (жироподавление) · ПОСЛЕ контраста — накопление', kind: 't1', contrast: true },
    ],
  },

  innerear: {
    name: 'Внутреннее ухо / лабиринт',
    note: '\u26a0 ЧЕРНОВИК протокол лабиринта: высокоразрешающая цистернография (FIESTA/CISS) субмиллиметрово. Гидропс-протоколы утверждает врач центра.',
    reqs: [
      { label: 'FIESTA / CISS — субмиллиметровая (\u2264 0.8 мм), улитка и полукружные каналы', kind: 'ssfp', maxThk: 0.8 },
      { label: 'T2 аксиально — обзор', kind: 't2', plane: 'axial' },
    ],
  },

  cvt: {
    name: 'Венозный тромбоз (ЦВТ)',
    note: '\u26a0 ЧЕРНОВИК протокол церебрального венозного тромбоза: MRV (венография), SWI/T2* на тромб, T1/T2 на сигнал сгустка. Утверждает врач центра.',
    reqs: [
      { label: 'MRV — проходимость синусов и вен', kind: 'mra' },
      { label: 'SWI / T2* — тромб, венозный застой', kind: 'swi' },
      { label: 'FLAIR — венозный инфаркт, отёк', kind: 'flair' },
    ],
  },

  encephalitis: {
    name: 'Менингит / энцефалит',
    note: '\u26a0 ЧЕРНОВИК протокол нейроинфекции: FLAIR, DWI, T1 до и после контраста (лептоменингеальное накопление), SWI на кровоизлияния. Утверждает врач центра.',
    reqs: [
      { label: 'FLAIR — паренхиматозные изменения, отёк', kind: 'flair' },
      { label: 'DWI / ADC — абсцесс, цитотоксический отёк', kind: 'dwi' },
      { label: 'T1 · ПОСЛЕ контраста — менингеальное/паренхиматозное накопление', kind: 't1', contrast: true },
      { label: 'SWI / T2* — геморрагический компонент', kind: 'swi' },
    ],
  },

  dementia: {
    name: 'Деменция / нейродегенерация',
    note: '\u26a0 ЧЕРНОВИК протокол деменции: 3D T1 (1 мм) для объёмометрии/атрофии, FLAIR, коронарная T2 по оси гиппокампа, SWI на микрокровоизлияния. Утверждает врач центра.',
    reqs: [
      { label: '3D T1 \u2264 1 мм — объёмометрия, атрофия (гиппокамп, кора)', kind: 't1', maxThk: 1.0 },
      { label: 'FLAIR — сосудистая нагрузка белого вещества', kind: 'flair' },
      { label: 'Корональная T2 (ось гиппокампа) — медиальная височная атрофия', kind: 't2', plane: 'coronal' },
      { label: 'SWI / T2* — микрокровоизлияния, амилоидная ангиопатия', kind: 'swi' },
    ],
  },

  vascular: {
    name: 'Сосудистые мальформации (АВМ/кавернома)',
    note: '\u26a0 ЧЕРНОВИК протокол сосудистых мальформаций: SWI (каверномы, венозные аномалии), TOF-MRA (АВМ, питающие сосуды), T2. ДСА/контрастную MRA утверждает врач центра.',
    reqs: [
      { label: 'SWI / T2* — каверномы, венозный компонент, гемосидерин', kind: 'swi' },
      { label: 'MRA (TOF) — АВМ, питающие и дренирующие сосуды', kind: 'mra' },
      { label: 'T2 — соотношение с паренхимой', kind: 't2' },
    ],
  },
};

let _activeProfile = 'standard';

// О каком исследовании последний раз спрашивали. Нужно, чтобы смена профиля в
// открытом окне перестраивала отчёт про ТО ЖЕ исследование, а не съезжала на
// «все серии разом» — иначе врач меняет профиль и незаметно получает разбор о
// другом.
let _последнийРазбор = {};

// [6.3.1] Пользовательские правки профилей хранятся в localStorage и
// накладываются поверх встроенных. Врач может менять требования, добавлять
// свои профили и удалять их — не трогая код.
const _PROFILE_STORE_KEY = 'protocol.profiles.v1';

function _loadUserProfiles() {
  try {
    const raw = localStorage.getItem(_PROFILE_STORE_KEY);
    if (!raw) return;
    const saved = JSON.parse(raw);
    if (saved && typeof saved === 'object') {
      for (const [k, v] of Object.entries(saved)) {
        if (v && Array.isArray(v.reqs)) PROTOCOL_PROFILES[k] = v;
      }
    }
  } catch (e) { /* повреждённое хранилище — игнорируем */ }
}

function _saveUserProfiles() {
  // Сохраняем ВСЕ профили целиком (встроенные + правки + пользовательские),
  // чтобы правки встроенных тоже переживали перезагрузку
  try { localStorage.setItem(_PROFILE_STORE_KEY, JSON.stringify(PROTOCOL_PROFILES)); }
  catch (e) { /* приватный режим */ }
}

_loadUserProfiles();

export function setActiveProfile(id) { if (PROTOCOL_PROFILES[id]) _activeProfile = id; }
export function getActiveProfile() { return _activeProfile; }

/**
 * Сверка состава исследования с чек-листом.
 *
 * @param {{studyUID?: string}} [o] — если указано, считается ТОЛЬКО по сериям
 *   этого исследования. Без него — по всем загруженным, как было: в обычном
 *   режиме исследование одно, и ограничивать нечего.
 */
export async function buildProtocolReport(o = {}) {
  const all = _getAllSeries() || [];
  await _ensureMeta(all);
  const list = all.map(analyzeSeries);
  const profile = PROTOCOL_PROFILES[_activeProfile] || PROTOCOL_PROFILES.standard;
  return buildRows(list, profile, { studyUID: o.studyUID || '' });
}

/** Короткая оценка одного исследования — то, что помещается в углу окна. */
export async function studyProtocolSummary(studyUID) {
  const { rows, series } = await buildProtocolReport({ studyUID });
  return { ...summarize(rows), rows, series, studyUID, profile: getActiveProfile() };
}

// ── UI: модальный отчёт ───────────────────────────────────────

/**
 * @param {{studyUID?: string, title?: string}} [o] — из окна сравнения приходит
 *   конкретное исследование: разбор должен быть про него, а не про всё сразу.
 */
async function _showReport(o = {}) {
  // [10.19.1] Кнопка «Протокол» без указания исследования при ДВУХ и более
  // загруженных исследованиях раньше строила отчёт ПО ОБЩЕЙ КУЧЕ — ровно та
  // ложь, которую чинил 10.17.0, только через другую дверь: требования одного
  // исследования закрывались сериями другого, и никакого предупреждения.
  // Теперь: одно исследование — как раньше; несколько — отчёт по самому
  // свежему, с предупреждением и кнопками перехода к остальным.
  let другие = null;
  if (!o.studyUID) {
    try {
      const все = sortStudiesByDate(buildStudyIndex(_getAllSeries() || []));
      if (все.length > 1) {
        o = { ...o, studyUID: все[0].studyUID, title: formatStudyTitle(все[0]) };
        другие = все;
      }
    } catch (e) { /* не разложилось — поведение как раньше */ }
  }
  _последнийРазбор = o;
  showToast('Протокол-контроль: анализ серий…', 'info', 1500);
  const { rows, series } = await buildProtocolReport({ studyUID: o.studyUID });
  if (!series.length) {
    showToast(o.studyUID ? 'В этом исследовании нет серий для разбора' : 'Сначала загрузите исследование',
      'warning', 2500);
    return;
  }

  document.getElementById('protocol-report-modal')?.remove();
  const wrap = document.createElement('div');
  wrap.id = 'protocol-report-modal';
  wrap.style.cssText = 'position:fixed;inset:0;z-index:10000;background:rgba(0,0,0,0.6);display:flex;align-items:center;justify-content:center;backdrop-filter:blur(3px);';
  const esc = (x) => String(x).replace(/\uFFFD/g, '').replace(/&/g, '&amp;').replace(/</g, '&lt;');

  const reqHtml = rows.map(r =>
    `<tr><td style="padding:5px 8px;white-space:nowrap;">${ICONS[r.status]}</td>
     <td style="padding:5px 8px;">${esc(r.label)}</td>
     <td style="padding:5px 8px;color:#94a3b8;">${esc(r.detail)}</td></tr>`).join('');

  const PLANE_RU = { axial: 'Ax', coronal: 'Cor', sagittal: 'Sag', '': '—' };
  const KIND_RU = { t1: 'T1', t2: 'T2', flair: 'FLAIR', dwi: 'DWI', mra: 'MRA/MRV', ssfp: 'FIESTA/CISS', swi: 'SWI/T2*', scout: 'скаут', other: '—' };
  const serHtml = series.map(x =>
    `<tr>
      <td style="padding:4px 8px;">${esc(x.desc.slice(0, 34)) || '—'}</td>
      <td style="padding:4px 8px;text-align:center;">${KIND_RU[x.kind] || x.kind.toUpperCase()}</td>
      <td style="padding:4px 8px;text-align:center;">${PLANE_RU[x.plane] ?? x.plane}</td>
      <td style="padding:4px 8px;text-align:center;">${x.count}</td>
      <td style="padding:4px 8px;text-align:center;">${Number.isFinite(x.thk) ? x.thk.toFixed(2) : '—'}</td>
      <td style="padding:4px 8px;text-align:center;">${Number.isFinite(x.gap) ? (x.gap >= 0 ? '+' : '') + x.gap.toFixed(2) : '—'}</td>
      <td style="padding:4px 8px;text-align:center;">${x.hasContrast ? '+C' : '—'}</td>
      <td style="padding:4px 8px;text-align:center;">${x.isLocalizer ? 'скаут' : (x.isPrimary ? 'ORIG' : (x.isDerived ? 'derived' : '?'))}</td>
    </tr>`).join('');

  const profileNote = (PROTOCOL_PROFILES[getActiveProfile()] || {}).note || '';
  wrap.innerHTML = `
  <div style="background:#0f1621;border:1px solid #2a3648;border-radius:12px;max-width:860px;width:94%;max-height:84vh;overflow-y:auto;padding:20px 24px;color:#dfe8f2;font-size:13px;box-shadow:0 20px 60px rgba(0,0,0,0.6);">
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;">
      <div style="font-size:15px;font-weight:600;">Протокол-контроль${o.title ? ': ' + esc(o.title) : ' исследования'}</div>
      <div style="display:flex;gap:6px;">
        <button id="protocol-edit-btn" style="background:none;border:1px solid #2a3648;border-radius:6px;color:#94a3b8;font-size:12px;padding:2px 10px;cursor:pointer;">✎ Редактировать профили</button>
        <button id="protocol-report-close" style="background:none;border:1px solid #2a3648;border-radius:6px;color:#94a3b8;font-size:14px;padding:2px 10px;cursor:pointer;">✕</button>
      </div>
    </div>
    <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
      <span style="color:#94a3b8;">Профиль:</span>
      <select id="protocol-profile-select" style="background:#182231;color:#dfe8f2;border:1px solid #2a3648;border-radius:6px;padding:3px 8px;font-size:13px;cursor:pointer;max-width:60%;">
        <optgroup label="Утверждённые">
          ${['standard', 'petroclival'].map(k => PROTOCOL_PROFILES[k] ? `<option value="${k}"${k === getActiveProfile() ? ' selected' : ''}>${esc(PROTOCOL_PROFILES[k].name)}</option>` : '').join('')}
        </optgroup>
        <optgroup label="Черновики (утверждает врач)">
          ${Object.entries(PROTOCOL_PROFILES).filter(([k]) => k !== 'standard' && k !== 'petroclival').map(([k, p]) => `<option value="${k}"${k === getActiveProfile() ? ' selected' : ''}>${esc(p.name)}</option>`).join('')}
        </optgroup>
      </select>
    </div>
    ${profileNote ? `<div style="color:${/ЧЕРНОВИК/.test(profileNote) ? '#f0b445' : '#7c8ba0'};font-size:12px;margin-bottom:10px;${/ЧЕРНОВИК/.test(profileNote) ? 'border-left:3px solid #f0b445;padding-left:8px;' : ''}">${esc(profileNote)}</div>` : ''}
    <table style="border-collapse:collapse;width:100%;margin-bottom:16px;">${reqHtml}</table>
    <div style="font-weight:600;margin:6px 0;">Серии исследования (${series.length})</div>
    <table style="border-collapse:collapse;width:100%;font-size:12px;">
      <tr style="color:#94a3b8;text-align:left;"><th style="padding:4px 8px;">Описание</th><th>Тип</th><th>Плоск.</th><th>Срезов</th><th>Толщ., мм</th><th>Зазор, мм</th><th>Контраст</th><th>ImageType</th></tr>
      ${serHtml}
    </table>
    <div style="color:#5b6b80;font-size:11px;margin-top:12px;">⚠️ — близко к норме (например, толщина 1.2 мм при требовании 1 мм) или параметр не удалось определить по тегам. Отрицательный зазор = перекрытие срезов, засчитывается как «интервал 0».</div>
  </div>`;
  if (другие) {
    const esc2 = (x) => String(x).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
    const баннер = document.createElement('div');
    баннер.style.cssText = 'margin:0 0 10px;padding:7px 10px;border-left:3px solid #f0b445;'
      + 'background:rgba(240,180,69,.08);color:#f0c674;font-size:12px;border-radius:6px;';
    баннер.innerHTML = `⚠ Загружено исследований: ${другие.length}. Этот отчёт — только по «${esc2(o.title || 'самому свежему')}»: `
      + 'смешивать их значило бы закрывать требования одного сериями другого. Остальные: '
      + другие.filter((ст) => ст.studyUID !== o.studyUID)
        .map((ст) => `<button data-study="${esc2(ст.studyUID)}" style="margin:2px 4px;padding:3px 8px;border-radius:6px;`
          + `border:1px solid #2a3648;background:#182231;color:#8fb4ff;cursor:pointer;font-size:11.5px;">${esc2(formatStudyTitle(ст))}</button>`)
        .join('');
    баннер.addEventListener('click', (e) => {
      const b = e.target.closest('button[data-study]');
      if (b) { wrap.remove(); _showReport({ studyUID: b.dataset.study }); }
    });
    const внутрь = wrap.firstElementChild;
    внутрь?.insertBefore(баннер, внутрь.children[1] || null);
  }
  wrap.addEventListener('click', (e) => { if (e.target === wrap) wrap.remove(); });
  wrap.querySelector('#protocol-report-close').addEventListener('click', () => wrap.remove());
  wrap.querySelector('#protocol-edit-btn')?.addEventListener('click', () => { wrap.remove(); _showProfileEditor(); });
  wrap.querySelector('#protocol-profile-select')?.addEventListener('change', (e) => {
    setActiveProfile(e.target.value);
    wrap.remove();
    _showReport(_последнийРазбор); // перестроить под выбранный профиль, не теряя исследование
  });
  document.body.appendChild(wrap);
}

// ── Редактор профилей [6.3.1] ─────────────────────────────────

const KIND_OPTIONS = [
  ['t1', 'T1'], ['t2', 'T2'], ['flair', 'FLAIR'], ['dwi', 'DWI'],
  ['mra', 'MRA/MRV'], ['ssfp', 'FIESTA/CISS'], ['swi', 'SWI/T2*'],
];
const PLANE_OPTIONS = [['', 'любая'], ['axial', 'аксиальная'], ['coronal', 'корональная'], ['sagittal', 'сагиттальная']];
const CONTRAST_OPTIONS = [['', 'не важно'], ['no', 'без контраста'], ['yes', 'с контрастом']];
const BUILTIN_KEYS = ['standard', 'petroclival', 'pituitary', 'epilepsy', 'trigeminal', 'stroke', 'ms', 'mass', 'cpa', 'orbit', 'innerear', 'cvt', 'encephalitis', 'dementia', 'vascular'];

function _showProfileEditor(editKey) {
  document.getElementById('protocol-editor-modal')?.remove();
  const wrap = document.createElement('div');
  wrap.id = 'protocol-editor-modal';
  wrap.style.cssText = 'position:fixed;inset:0;z-index:10001;background:rgba(0,0,0,0.65);display:flex;align-items:center;justify-content:center;backdrop-filter:blur(3px);';
  const esc = (x) => String(x).replace(/\uFFFD/g, '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');

  const key = editKey || Object.keys(PROTOCOL_PROFILES)[0];
  const prof = PROTOCOL_PROFILES[key] || { name: '', reqs: [] };
  const isBuiltin = BUILTIN_KEYS.includes(key);

  const reqRow = (r, i) => `
    <div class="pe-req" data-i="${i}" style="display:grid;grid-template-columns:1fr 90px 110px 120px 70px 60px 60px 28px;gap:6px;align-items:center;padding:5px 0;border-bottom:1px solid #1c2735;">
      <input class="pe-label" value="${esc(r.label || '')}" placeholder="Название требования" style="background:#182231;color:#dfe8f2;border:1px solid #2a3648;border-radius:5px;padding:4px 6px;font-size:12px;">
      <select class="pe-kind" style="background:#182231;color:#dfe8f2;border:1px solid #2a3648;border-radius:5px;padding:4px;font-size:12px;">
        ${KIND_OPTIONS.map(([v, n]) => `<option value="${v}"${r.kind === v ? ' selected' : ''}>${n}</option>`).join('')}
      </select>
      <select class="pe-plane" style="background:#182231;color:#dfe8f2;border:1px solid #2a3648;border-radius:5px;padding:4px;font-size:12px;">
        ${PLANE_OPTIONS.map(([v, n]) => `<option value="${v}"${(r.plane || '') === v ? ' selected' : ''}>${n}</option>`).join('')}
      </select>
      <select class="pe-contrast" style="background:#182231;color:#dfe8f2;border:1px solid #2a3648;border-radius:5px;padding:4px;font-size:12px;">
        ${CONTRAST_OPTIONS.map(([v, n]) => `<option value="${v}"${(r.contrast === true ? 'yes' : r.contrast === false ? 'no' : '') === v ? ' selected' : ''}>${n}</option>`).join('')}
      </select>
      <input class="pe-thk" type="number" step="0.1" value="${Number.isFinite(r.maxThk) ? r.maxThk : ''}" placeholder="мм" title="Макс. толщина, мм" style="background:#182231;color:#dfe8f2;border:1px solid #2a3648;border-radius:5px;padding:4px;font-size:12px;">
      <label style="font-size:11px;color:#94a3b8;text-align:center;"><input class="pe-gap" type="checkbox"${r.needGap0 ? ' checked' : ''}> инт.0</label>
      <label style="font-size:11px;color:#94a3b8;text-align:center;"><input class="pe-prim" type="checkbox"${r.needPrimary ? ' checked' : ''}> ориг.</label>
      <button class="pe-del" title="Удалить требование" style="background:none;border:1px solid #3a2833;border-radius:5px;color:#e07a8a;cursor:pointer;font-size:13px;">✕</button>
    </div>`;

  wrap.innerHTML = `
  <div style="background:#0f1621;border:1px solid #2a3648;border-radius:12px;max-width:920px;width:96%;max-height:88vh;overflow-y:auto;padding:20px 24px;color:#dfe8f2;font-size:13px;box-shadow:0 20px 60px rgba(0,0,0,0.6);">
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:14px;">
      <div style="font-size:15px;font-weight:600;">Редактор профилей протокол-контроля</div>
      <button id="pe-close" style="background:none;border:1px solid #2a3648;border-radius:6px;color:#94a3b8;font-size:14px;padding:2px 10px;cursor:pointer;">✕</button>
    </div>
    <div style="display:flex;gap:8px;align-items:center;margin-bottom:12px;">
      <span style="color:#94a3b8;">Профиль:</span>
      <select id="pe-select" style="background:#182231;color:#dfe8f2;border:1px solid #2a3648;border-radius:6px;padding:4px 8px;font-size:13px;cursor:pointer;">
        ${Object.entries(PROTOCOL_PROFILES).map(([k, p]) => `<option value="${k}"${k === key ? ' selected' : ''}>${esc(p.name)}${BUILTIN_KEYS.includes(k) ? '' : ' (свой)'}</option>`).join('')}
      </select>
      <button id="pe-new" style="background:#182231;border:1px solid #2a3648;border-radius:6px;color:#8fb4ff;padding:4px 10px;font-size:12px;cursor:pointer;">+ Новый профиль</button>
      ${!isBuiltin ? '<button id="pe-delete-profile" style="background:none;border:1px solid #3a2833;border-radius:6px;color:#e07a8a;padding:4px 10px;font-size:12px;cursor:pointer;">Удалить профиль</button>' : ''}
    </div>
    <div style="margin-bottom:8px;">
      <label style="color:#94a3b8;font-size:12px;">Название профиля:</label>
      <input id="pe-name" value="${esc(prof.name || '')}" style="width:100%;background:#182231;color:#dfe8f2;border:1px solid #2a3648;border-radius:6px;padding:6px 8px;font-size:13px;margin-top:3px;box-sizing:border-box;">
    </div>
    <div style="color:#f0b445;font-size:11px;border-left:3px solid #f0b445;padding-left:8px;margin-bottom:12px;">
      ⚠ Составы протоколов и пороги — клиническое решение. Их обязан утвердить рентгенолог/нейрохирург вашего центра. Программа проверяет полноту, а не назначает протокол.
    </div>
    <div style="display:grid;grid-template-columns:1fr 90px 110px 120px 70px 60px 60px 28px;gap:6px;font-size:10px;color:#6b7a8c;padding-bottom:4px;">
      <span>Требование</span><span>Тип</span><span>Плоскость</span><span>Контраст</span><span>Толщ.≤</span><span>Инт.0</span><span>Ориг.</span><span></span>
    </div>
    <div id="pe-reqs">${prof.reqs.map(reqRow).join('')}</div>
    <button id="pe-add-req" style="background:#182231;border:1px dashed #2a3648;border-radius:6px;color:#8fb4ff;padding:6px 10px;font-size:12px;cursor:pointer;margin-top:8px;width:100%;">+ Добавить требование</button>
    <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:16px;">
      <button id="pe-save" style="background:#2563eb;border:none;border-radius:6px;color:#fff;padding:8px 18px;font-size:13px;cursor:pointer;font-weight:500;">Сохранить</button>
    </div>
  </div>`;

  const reqsEl = () => wrap.querySelector('#pe-reqs');
  const addReqRow = (r = {}) => {
    const div = document.createElement('div');
    div.innerHTML = reqRow(r, reqsEl().children.length);
    const node = div.firstElementChild;
    node.querySelector('.pe-del').addEventListener('click', () => node.remove());
    reqsEl().appendChild(node);
  };

  // собрать текущую форму в объект профиля
  const collect = () => {
    const reqs = [...reqsEl().querySelectorAll('.pe-req')].map(row => {
      const req = { label: row.querySelector('.pe-label').value.trim(), kind: row.querySelector('.pe-kind').value };
      const plane = row.querySelector('.pe-plane').value; if (plane) req.plane = plane;
      const c = row.querySelector('.pe-contrast').value; if (c === 'yes') req.contrast = true; else if (c === 'no') req.contrast = false;
      const thk = parseFloat(row.querySelector('.pe-thk').value); if (Number.isFinite(thk)) req.maxThk = thk;
      if (row.querySelector('.pe-gap').checked) req.needGap0 = true;
      if (row.querySelector('.pe-prim').checked) req.needPrimary = true;
      return req;
    }).filter(r => r.label);
    return { name: wrap.querySelector('#pe-name').value.trim() || 'Без названия', reqs };
  };

  const close = () => wrap.remove();
  wrap.addEventListener('click', (e) => { if (e.target === wrap) close(); });
  wrap.querySelector('#pe-close').addEventListener('click', close);
  wrap.querySelectorAll('.pe-del').forEach(b => b.addEventListener('click', (e) => e.target.closest('.pe-req').remove()));
  wrap.querySelector('#pe-add-req').addEventListener('click', () => addReqRow());

  // смена профиля в редакторе — сохранить текущий, открыть выбранный
  wrap.querySelector('#pe-select').addEventListener('change', (e) => {
    PROTOCOL_PROFILES[key] = collect(); _saveUserProfiles();
    close(); _showProfileEditor(e.target.value);
  });

  wrap.querySelector('#pe-new')?.addEventListener('click', () => {
    PROTOCOL_PROFILES[key] = collect();
    const newKey = 'custom_' + Date.now();
    PROTOCOL_PROFILES[newKey] = { name: 'Новый профиль', reqs: [] };
    _saveUserProfiles(); close(); _showProfileEditor(newKey);
  });

  wrap.querySelector('#pe-delete-profile')?.addEventListener('click', () => {
    delete PROTOCOL_PROFILES[key];
    if (_activeProfile === key) _activeProfile = 'standard';
    _saveUserProfiles(); close(); _showProfileEditor();
  });

  wrap.querySelector('#pe-save').addEventListener('click', () => {
    PROTOCOL_PROFILES[key] = collect();
    _saveUserProfiles();
    setActiveProfile(key);
    close();
    showToast('Профиль сохранён', 'success', 1500);
    _showReport(_последнийРазбор); // показать отчёт по сохранённому профилю
  });

  document.body.appendChild(wrap);
}

/** Открыть разбор конкретного исследования — зовёт режим сравнения. */
export function showProtocolReport(o = {}) {
  return Promise.resolve()
    .then(() => _showReport(o))
    .catch((e) => { log.warn('[Protocol]', e.message); showToast('Ошибка протокол-контроля: ' + e.message, 'error', 3000); });
}

export function initProtocolCheck(ctx) {
  if (typeof ctx?.getAllSeries === 'function') _getAllSeries = ctx.getAllSeries;
  const btn = document.getElementById('btn-protocol-check');
  if (btn) btn.addEventListener('click', () => showProtocolReport({}));
}
